<script setup lang="ts">
import { computed } from 'vue';
import { UiDialog, UiText, UiIcon } from '@shopexperts/shared';

export type DeleteTarget = 'category' | 'subcategory';

const props = defineProps<{
  target: DeleteTarget;
  name: string;
  subcategoryCount?: number;
}>();

const open = defineModel<boolean>({ default: false });
const isLoading = defineModel<boolean>('loading', { default: false });

const emit = defineEmits<{
  confirm: [];
}>();

const title = computed(() =>
  props.target === 'category' ? 'Delete Category' : 'Delete Subcategory'
);

const warningMessage = computed(() => {
  if (props.target === 'category') {
    const subText = props.subcategoryCount
      ? ` and its ${props.subcategoryCount} subcategor${props.subcategoryCount === 1 ? 'y' : 'ies'}`
      : '';
    return `This will delete "${props.name}"${subText}. This is a soft delete — the category will be hidden from the platform.`;
  }
  return `This will delete "${props.name}". This is a soft delete — the subcategory will be hidden from the platform.`;
});

const impactMessage = computed(() => {
  if (props.target === 'category') {
    return 'Experts who have selected this category or any of its subcategories will no longer see them on their profiles. This change takes effect immediately.';
  }
  return 'Experts who have selected this subcategory will no longer see it on their profiles. This change takes effect immediately.';
});
</script>

<template>
  <UiDialog
    v-model="open"
    v-model:loading="isLoading"
    :title="title"
    primaryActionLabel="Delete"
    secondaryActionLabel="Cancel"
    size="sm"
    @primaryAction="emit('confirm')"
    @secondaryAction="open = false"
    @close="open = false"
  >
    <div class="flex flex-col gap-3">
      <UiText as="p" variant="p-small" class="text-gray-700">
        {{ warningMessage }}
      </UiText>

      <div class="flex items-start gap-2.5 px-3 py-2.5 rounded-lg bg-amber-50 border border-amber-200">
        <UiIcon name="info-circle" class="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
        <UiText as="p" variant="caption-normal" class="text-amber-800">
          {{ impactMessage }}
        </UiText>
      </div>
    </div>
  </UiDialog>
</template>
